import { useState } from "react";
import useFetchAndTrace from "../../fetchWithTrace";
import settings from "../../../../constants/settings";
import { useCustomAlert } from "../../alerts";
import useManufacturingDashboard, {
  ManufacturingItem,
} from "../../manfacturingDashboard";

export default function useManufacturingPriority(onSuccess?: () => void) {
  const [updating, setUpdating] = useState(false);
  const { refreshDashboard } = useManufacturingDashboard();
  const fetchAndTrace = useFetchAndTrace();
  const customAlert = useCustomAlert();

  const updatePriorityHandler = (response: Response) => {
    setUpdating(false);
    if (response.ok) {
      onSuccess?.();
      refreshDashboard();
    } else {
      throw Error("Failed to update manufacturing priority");
    }
  };

  const errorHandler = () => {
    setUpdating(false);
    customAlert("Failed to update manufacturing priority");
  };

  const optimizationHandler = async (response: Response) => {
    setUpdating(false);
    if (response.ok) {
      onSuccess?.();
      refreshDashboard();
    } else if (response.status === 400) {
      const { detail } = (await response.json()) as { detail: string };
      customAlert(detail);
    } else {
      throw Error("Failed to optimize allocations");
    }
  };

  const optimizationErrorHandler = () => {
    setUpdating(false);
    customAlert("Failed to optimize allocations");
  };

  const updateManufacturingPriority = (
    reorderedItems: ManufacturingItem[],
    alsoUpdateAllocations: boolean,
  ) => {
    setUpdating(true);
    void fetchAndTrace(
      `${settings.api_url}/manufacturing/reprioritize`,
      updatePriorityHandler,
      errorHandler,
      {
        method: "PUT",
        body: JSON.stringify({
          items: reorderedItems,
          optimize_allocations: alsoUpdateAllocations,
        }),
      },
    );
  };

  const optimizeAllocations = () => {
    setUpdating(true);
    void fetchAndTrace(
      `${settings.api_url}/manufacturing/optimize-allocations`,
      optimizationHandler,
      optimizationErrorHandler,
      {
        method: "PUT",
      },
    );
  };

  const optimizeAllocationsForShipment = (
    boardId: string,
    shipmentId: string,
  ) => {
    setUpdating(true);
    void fetchAndTrace(
      `${settings.api_url}/boards/${boardId}/shipments/${shipmentId}/optimize-allocations`,
      optimizationHandler,
      optimizationErrorHandler,
      {
        method: "PUT",
      },
    );
  };

  return {
    updateManufacturingPriority,
    optimizeAllocations,
    optimizeAllocationsForShipment,
    updating,
  };
}
